/**
 * Signed CloudFront URLs for private S3 media (studio, leather DIY, library).
 * Small images/thumbs: same-origin /data/gallery/… baked in index JSON (no backend, no CDN).
 * Large media: CloudFront signed URLs only by default (REACT_APP_GALLERY_STREAM_FALLBACK=1 for VPN proxy).
 */

import { API_URL } from '../config/env';
import { isLocalDiskDev } from '../config/localMediaDev';
import { isStreamBlocked, markStreamBlocked, parseRetryAfterSec, apiFetch, buildUrl } from './apiFetchPolicy';
import { resolveBakedImageUrl } from './galleryMediaBakedUrls';
import { getDefaultContentCache, isExpired } from '../content/cache';
import { buildGalleryStreamUrlWithToken, buildGalleryStreamFetchUrl } from './galleryStreamUrl';
// Inline shim over ContentCache (legacy signed-URL cache facade removed 2026-07-25).
// Keeps the legacy names this file already calls, so no call-site body had to change.
const _galleryCache = getDefaultContentCache();
const _galleryNs = 'gallery';
const _keyOf = (k) => String(k == null ? '' : k).replace(/^\/+/, '').trim();
const getCachedSignedUrl = (k) => _galleryCache.get(_galleryNs, _keyOf(k), '*');
const setCachedSignedUrl = (k, u, ttlMs) => _galleryCache.set(_galleryNs, _keyOf(k), '*', u, ttlMs, { source: 'cf-signed' });
const isCloudfrontSignedUrlExpired = (u, m) => isExpired(u, m);

const signedUrlCache = new Map();
const signedUrlPending = new Map();

/** Production default: direct CDN only; stream proxy is opt-in for restricted networks. */
export function isGalleryStreamFallbackEnabled() {
  return String(process.env.REACT_APP_GALLERY_STREAM_FALLBACK || '').trim() === '1';
}

function pickSignedUrl(data) {
  if (!data || typeof data !== 'object') return null;
  const url = data.url ?? data.photoUrl ?? data.signedUrl;
  return typeof url === 'string' && url.trim() ? url.trim() : null;
}

function createBearerGetClient(token) {
  return {
    async get(path, { params } = {}) {
      return {
        data: await apiFetch(buildUrl(`${API_URL}${path}`, params), {
          method: 'GET',
          token,
          credentials: 'omit',
        }),
      };
    },
  };
}

function isImageKey(key) {
  return /\.(jpe?g|webp|avif|png|gif)$/i.test(String(key || ''));
}

function isStreamableMediaKey(key) {
  return /\.(mp4|webm|mkv|m4a|mp3|ogg|wav|flac|aac|pdf)$/i.test(String(key || ''));
}

/**
 * True for the ECS disk mirror path `/oss-light/…`.
 *
 * That mirror only ever holds LIGHTWEIGHT objects (thumbs, covers, JSON). The
 * backend nevertheless short-circuits signing to an `/oss-light/` URL, so heavy
 * media (video/audio/PDF) can come back as an oss-light URL that 404s or resets
 * (and the host itself may be unreachable — e.g. Aliyun's ICP block page for
 * www.mgv.gallery). Never prefer those over the stream route.
 */
export function isOssLightUrl(url) {
  return /^https?:\/\/[^/]+\/oss-light\//i.test(String(url || ''));
}

/** An oss-light URL handed back for a heavy (streamable) object — unusable. */
export function isOssLightHeavyMediaUrl(url, key) {
  return isOssLightUrl(url) && isStreamableMediaKey(key);
}

/**
 * POST /gallery/media/signed-urls — batch-sign many keys in ONE request.
 * Anonymous allowed for public crafts covers. Uses the shared signed-URL cache;
 * only uncached/expired keys are sent. Returns a Map key → signed URL.
 */
export async function fetchGalleryMediaSignedUrlsBatch(token, s3Keys) {
  const keys = [...new Set((s3Keys || []).map((k) => String(k || '').replace(/^\/+/, '').trim()).filter(Boolean))];
  const result = new Map();
  if (!keys.length || isLocalDiskDev()) return result;

  const missing = [];
  for (const key of keys) {
    const cached = getCachedSignedUrl(key) || signedUrlCache.get(key);
    if (cached && !isCloudfrontSignedUrlExpired(cached)) {
      result.set(key, cached);
    } else {
      missing.push(key);
    }
  }
  if (!missing.length) return result;

  try {
    const res = await apiFetch(`${API_URL}/gallery/media/signed-urls`, {
      method: 'POST',
      token,
      credentials: 'omit',
      body: { keys: missing },
    });
    const urls = res?.urls || {};
    for (const [key, url] of Object.entries(urls)) {
      if (typeof url === 'string' && url) {
        result.set(key, url);
        signedUrlCache.set(key, url);
        setCachedSignedUrl(key, url);
      }
    }
  } catch (error) {
    console.warn('[studioMediaApi] batch signed-urls failed:', error?.message || error);
  }
  return result;
}

/**
 * GET /auth/gallery/media/signed-url?key=… (single canonical path — avoids duplicate mint requests).
 */
export async function fetchGalleryMediaSignedUrl(token, s3Key) {
  const key = String(s3Key || '').replace(/^\/+/, '').trim();
  if (!key) return null;
  if (isLocalDiskDev()) return null;
  if (!token) return null;

  const sessionCached = getCachedSignedUrl(key);
  if (sessionCached && !isCloudfrontSignedUrlExpired(sessionCached)) {
    signedUrlCache.set(key, sessionCached);
    return sessionCached;
  }
  const memHit = signedUrlCache.get(key);
  if (memHit && !isCloudfrontSignedUrlExpired(memHit)) return memHit;
  if (signedUrlPending.has(key)) return signedUrlPending.get(key);

  const client = createBearerGetClient(token);
  const params = { key };

  const pending = (async () => {
    const paths = ['/auth/gallery/media/signed-url', '/gallery/media/signed-url'];
    try {
      for (const apiPath of paths) {
        try {
          const res = await client.get(apiPath, { params });
          const url = pickSignedUrl(res.data);
          if (url) {
            signedUrlCache.set(key, url);
            setCachedSignedUrl(key, url);
            return url;
          }
        } catch (error) {
          const status = error?.response?.status;
          if (status === 429 || status === 503) {
            noteGalleryMediaStreamRateLimit(error);
            return null;
          }
          if (status !== 404 && status !== 401 && status !== 400) {
            return null;
          }
        }
      }
      return null;
    } finally {
      signedUrlPending.delete(key);
    }
  })();

  signedUrlPending.set(key, pending);
  return pending;
}

/** Static images/posters: baked same-origin path first, else signed CDN — never byte-stream proxy. */
export async function fetchGalleryMediaDisplayUrl(token, s3Key, entry = null) {
  const baked = resolveBakedImageUrl(s3Key, entry);
  if (baked) return baked;
  return fetchGalleryMediaSignedUrl(token, s3Key);
}

/** Build S3 key variants when the catalog omits file extension. */
export function buildAudioS3KeyCandidates(prefix, audioKey) {
  const keyPath = String(audioKey || '').replace(/^\/+/, '');
  const parts = keyPath.split('/');
  const entityId = parts[parts.length - 2];
  const fileName = parts[parts.length - 1] || '';
  const fileBase = fileName.replace(/\.(m4a|mp3|ogg|wav|flac|aac)$/i, '') || '';
  if (!entityId || !fileBase) return keyPath ? [keyPath] : [];

  const extMatch = fileName.match(/\.(m4a|mp3|ogg|wav|flac|aac)$/i);
  const preferredExt = extMatch ? extMatch[0].toLowerCase() : '.mp3';
  const exts = [preferredExt, '.m4a', '.mp3', '.ogg', '.wav'].filter((ext, i, arr) => arr.indexOf(ext) === i);

  return exts.map((ext) => `${prefix}/${entityId}/${fileBase}${ext}`);
}

export async function fetchSignedAudioUrl(token, audioKey, prefix) {
  if (isLocalDiskDev()) return null;
  const keys = buildAudioS3KeyCandidates(prefix, audioKey);
  for (const key of keys) {
    const url = await fetchGalleryMediaSignedUrl(token, key);
    if (url) return url;
  }
  return null;
}

/**
 * Stream-URL *formatting* is delegated to the shared galleryStreamUrl helper
 * (single definition of the /api/auth/gallery/media/stream?… shape). The signing,
 * rate-limit, and cache logic below remains the production source of truth.
 */

/** One canonical same-origin stream URL (Bearer via access_token). */
export function buildGalleryMediaStreamUrls(s3Key, token) {
  if (isStreamBlocked('gallery-media')) return [];
  const key = String(s3Key || '').replace(/^\/+/, '').trim();
  if (!key || !token) return [];
  return [buildGalleryStreamUrlWithToken(key, token)];
}

export function buildGalleryMediaStreamUrl(s3Key, token) {
  return buildGalleryMediaStreamUrls(s3Key, token)[0] || '';
}

/** fetch() with Authorization header — single route. */
export function buildGalleryMediaStreamFetchUrls(s3Key) {
  const key = String(s3Key || '').trim();
  if (!key) return [];
  return [buildGalleryStreamFetchUrl(key)];
}

/**
 * Playback: CloudFront signed URL (browser streams directly from CDN).
 * Stream proxy only when REACT_APP_GALLERY_STREAM_FALLBACK=1. Images never use stream proxy.
 */
export async function buildGalleryMediaPlaybackUrls(s3Key, token) {
  if (isStreamBlocked('gallery-media')) return [];
  const key = String(s3Key || '').replace(/^\/+/, '').trim();
  if (!key || !token) return [];

  if (isImageKey(key)) {
    const baked = resolveBakedImageUrl(key);
    if (baked) return [baked];
    const signed = await fetchGalleryMediaSignedUrl(token, key);
    return signed ? [signed] : [];
  }

  const signed = await fetchGalleryMediaSignedUrl(token, key);
  // `/oss-light/` is the lightweight-asset mirror: for video/audio it either
  // 404s or the host is unreachable (e.g. the Aliyun ICP block on
  // www.mgv.gallery), so it must never be the preferred — or only — candidate.
  const unusableSigned = Boolean(signed) && isOssLightHeavyMediaUrl(signed, key);

  if (signed && !unusableSigned && !isGalleryStreamFallbackEnabled()) return [signed];

  // Force the stream route even when the fallback flag is off: without it an
  // oss-light URL would be the sole candidate and playback could never start.
  const stream =
    isGalleryStreamFallbackEnabled() || unusableSigned
      ? buildGalleryMediaStreamUrl(key, token)
      : '';
  if (signed && stream) {
    return unusableSigned ? [stream, signed] : [signed, stream];
  }
  return [signed || stream].filter(Boolean);
}

export function noteGalleryMediaStreamRateLimit(responseOrError) {
  const status = responseOrError?.status ?? responseOrError?.response?.status;
  if (status !== 429 && status !== 503) return;
  const retryAfter =
    parseRetryAfterSec(responseOrError) ||
    parseRetryAfterSec(responseOrError?.response) ||
    3600;
  markStreamBlocked('gallery-media', retryAfter);
}

/** @deprecated Prefer buildGalleryMediaPlaybackUrls — returns at most one stream URL. */
export function buildGalleryMediaPlaybackStreamUrls(s3Key, token) {
  if (isImageKey(s3Key)) return [];
  if (!isStreamableMediaKey(s3Key)) return buildGalleryMediaStreamUrls(s3Key, token);
  return buildGalleryMediaStreamUrls(s3Key, token);
}

export { isImageKey, isStreamableMediaKey };
